import { Typography } from "@/components/common/Typography/Typography"
import {
  createDocumentTagMenuEntries,
  DocumentTagBadge,
  type DocumentTagEntry,
} from "@/components/feature/KnowledgebaseFilters/DocumentTagCategoryMenu"
import { getFileTypeIcon } from "@/components/feature/Modals/TagDocumentDialog/TagDocumentShared"
import { Checkbox } from "@/components/ui/checkbox"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { KnowledgebaseFile } from "@/types/knowledgebase"
import { DocumentTagsResponse } from "@/types/knowledgebaseCategories"
import { classNames } from "@/utils/classNames"
import { IconAlertCircle, IconLoader2 } from "@tabler/icons-react"
import { useMemo, useState } from "react"
import { AddTagButton } from "../AddTagButton"
import {
  KNOWLEDGEBASE_FILE_LIST_FIRST_COLUMN_CLASS,
  KNOWLEDGEBASE_FILE_LIST_FIRST_COLUMN_CLASS_NO_TAGS,
} from "./KnowledgebaseFileListLayout"

const MAX_VISIBLE_TAGS_PER_ROW = 3

interface FileInfoProps {
  item: KnowledgebaseFile
  isHover: boolean
  onTitleClick: () => void
  isTitleInteractive?: boolean
  showCheckbox?: boolean
  isSelected?: boolean
  onToggleSelect?: () => void
  isCheckboxDisabled?: boolean
  documentTags?: DocumentTagsResponse
  tagEntries?: DocumentTagEntry[]
  showAllTags?: boolean
  /** Category IDs whose tags should be hidden inline (shown in a dedicated column instead) */
  excludeCategoryIds?: string[]
  handleTag: () => void
  kbTaggingFlowEnabled?: boolean
}

export const FileInfo: React.FC<FileInfoProps> = ({
  item,
  isHover,
  onTitleClick,
  isTitleInteractive = true,
  showCheckbox,
  isSelected,
  onToggleSelect,
  isCheckboxDisabled,
  documentTags,
  tagEntries,
  showAllTags = true,
  excludeCategoryIds,
  handleTag,
  kbTaggingFlowEnabled = true,
}) => {
  const { translate } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const allTagEntries = useMemo(() => {
    if (tagEntries) {
      return tagEntries.filter(
        (entry) => !excludeCategoryIds?.includes(entry.category.categoryId),
      )
    }

    if (!documentTags?.categories) return []

    return createDocumentTagMenuEntries(documentTags.categories).filter(
      (entry) => {
        const isUnapprovedAuto =
          entry.tag.source === "auto" && entry.tag.approvedAt === null
        // Unapproved auto tags always show, regardless of active columns.
        if (isUnapprovedAuto) return true
        // Approved / user tags respect the column exclusion list.
        if (excludeCategoryIds?.includes(entry.category.categoryId))
          return false
        return entry.tag.source === "auto" || entry.tag.source === "user"
      },
    )
  }, [documentTags, excludeCategoryIds, tagEntries])

  const visibleTags = isExpanded
    ? allTagEntries
    : allTagEntries.slice(0, MAX_VISIBLE_TAGS_PER_ROW)
  const hiddenTagCount = allTagEntries.length - visibleTags.length

  const renderTag = (entry: DocumentTagEntry) => (
    <span
      key={entry.tag.id}
      onClick={(e) => {
        e.stopPropagation()
        handleTag()
      }}
      className="pointer-events-auto"
    >
      <span className="pointer-events-none">
        <DocumentTagBadge entry={entry} />
      </span>
    </span>
  )

  return (
    <div
      className={classNames(
        showAllTags && visibleTags.length
          ? KNOWLEDGEBASE_FILE_LIST_FIRST_COLUMN_CLASS
          : KNOWLEDGEBASE_FILE_LIST_FIRST_COLUMN_CLASS_NO_TAGS,
      )}
    >
      <div className="flex h-6 w-6 shrink-0 items-center justify-center">
        {showCheckbox ? (
          <Checkbox
            checked={isSelected}
            disabled={isCheckboxDisabled}
            onCheckedChange={() => {
              if (!isCheckboxDisabled) onToggleSelect?.()
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            aria-label={`${translate("KNOWLEDGEBASE/FILE_ARIA_LABEL_ITEM")} ${item.fileName}`}
            className="h-5 w-5"
          />
        ) : item.ingestionStatus === "processing" ||
          item.ingestionStatus === "retrying" ? (
          <IconLoader2
            size={24}
            className="animate-spin text-muted-foreground"
            aria-label={translate("KNOWLEDGEBASE/STATUS_PROCESSING")}
          />
        ) : item.ingestionStatus === "failed" ? (
          <IconAlertCircle
            size={24}
            className="text-destructive"
            aria-label={translate("KNOWLEDGEBASE/STATUS_FAILED")}
          />
        ) : (
          (() => {
            const FileIcon = getFileTypeIcon(item.fileName)
            return (
              <FileIcon size={22} className="text-primary" aria-hidden="true" />
            )
          })()
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-start gap-1">
        {isTitleInteractive ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onTitleClick()
            }}
            onKeyDown={(event) => event.stopPropagation()}
            className={classNames(
              "w-fit max-w-full text-left hover:underline",
              "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-yellow1",
            )}
          >
            <Typography as="span" variant="textS" className="block truncate">
              {item.fileName}
            </Typography>
          </button>
        ) : (
          <Typography as="span" variant="textS" className="block truncate">
            {item.fileName}
          </Typography>
        )}
        {item.ingestionStatus === "processing" && (
          <Typography variant="textXS" className="text-muted-foreground" as="p">
            {translate("KNOWLEDGEBASE/STATUS_PROCESSING_LABEL")}
          </Typography>
        )}
        {item.ingestionStatus === "retrying" && (
          <Typography variant="textXS" className="text-muted-foreground" as="p">
            {translate("KNOWLEDGEBASE/STATUS_RETRYING_LABEL")}
          </Typography>
        )}
        {item.ingestionStatus === "failed" && (
          <span title={item.ingestionError || undefined}>
            <Typography variant="textXS" className="text-destructive" as="p">
              {translate("KNOWLEDGEBASE/STATUS_INGESTION_FAILED")}
            </Typography>
          </span>
        )}
        {isHover && item.sharepointPath && (
          <Typography variant={"textXS"} className="text-grey5" as="p">
            {item.sharepointPath}
          </Typography>
        )}
        {showAllTags && (isHover || visibleTags.length > 0) && (
          <div
            className="flex flex-wrap items-center gap-1"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {kbTaggingFlowEnabled && isHover ? (
              <AddTagButton onClick={handleTag} />
            ) : null}
            {visibleTags.map(renderTag)}
            {hiddenTagCount > 0 && (
              <button
                type="button"
                onClick={() => setIsExpanded(true)}
                className="mt-1 self-center text-[10px] font-medium text-grey5 transition-colors hover:text-yellow1 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-yellow1"
              >
                +{hiddenTagCount}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
